// src/pages/materials/components/ColorSelect.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Modal, Select, Tag, Tooltip, Segmented, Space, Button } from "antd";

export default function ColorSelect({
  value,
  onChange,
  multiple = true,
  placeholder = "Оберіть колір / узор",
  allowClear = true,
  maxTagCount = "responsive",
  kind = "both", // 'colors' | 'patterns' | 'both'
}) {
  const [activeTab, setActiveTab] = useState(kind === "patterns" ? "patterns" : "colors");
  const [open, setOpen] = useState(false);

  // не даём селекту фокус → не всплывает клавиатура
  const selectRef = useRef(null);
  const handleSelectMouseDown = (e) => {
    // кликом просто открываем наш Modal, фокус инпуту не даём
    e.preventDefault();
    setOpen(true);
  };

  useEffect(() => {
    if (kind !== "both") setActiveTab(kind === "patterns" ? "patterns" : "colors");
  }, [kind]);

  // ===== палитра и узоры =====
  const palette = useMemo(
    () => [
      { name: "Чорний", value: "#000000" },
      { name: "Білий", value: "#ffffff", border: true },
      { name: "Червоний", value: "#ff4d4f" },
      { name: "Темно-червоний", value: "#a8071a" },
      { name: "Помаранчевий", value: "#fa8c16" },
      { name: "Жовтий", value: "#fadb14" },
      { name: "Лайм", value: "#a0d911" },
      { name: "Зелений", value: "#52c41a" },
      { name: "Бірюзовий", value: "#13c2c2" },
      { name: "Блакитний", value: "#1890ff" },
      { name: "Синій", value: "#2f54eb" },
      { name: "Фіолетовий", value: "#722ed1" },
      { name: "Рожевий", value: "#eb2f96" },
      { name: "Сірий", value: "#8c8c8c" },
      { name: "Темно-сірий", value: "#595959" },
      { name: "Світло-сірий", value: "#d9d9d9", border: true },
      { name: "Коричневий", value: "#8B4513" },
      { name: "Оливковий", value: "#808000" },
      { name: "Бежевий", value: "#f5f5dc", border: true },
      { name: "Вино", value: "#800020" },
    ],
    []
  );

  const patterns = useMemo(
    () => [
      {
        key: "stripes",
        name: "Смуги",
        style: {
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(0,0,0,0.85) 0 8px, rgba(255,255,255,0.85) 8px 16px)",
          backgroundColor: "#ddd",
        },
      },
      {
        key: "dots",
        name: "Горох",
        style: {
          backgroundColor: "#fff",
          backgroundImage: "radial-gradient(#000 2px, transparent 3px)",
          backgroundSize: "12px 12px",
        },
      },
      {
        key: "grid",
        name: "Клітинка",
        style: {
          backgroundColor: "#f5f5f5",
          backgroundImage:
            "linear-gradient(#ccc 1px, transparent 1px), linear-gradient(90deg, #ccc 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        },
      },
      {
        key: "chevron",
        name: "Ялинка",
        style: {
          backgroundImage:
            "repeating-linear-gradient(45deg, #555 0 8px, #999 8px 16px), repeating-linear-gradient(-45deg, #777 0 8px, #bbb 8px 16px)",
          mixBlendMode: "multiply",
        },
      },
      {
        key: "houndstooth",
        name: "Гусине лапка",
        style: {
          backgroundImage:
            "conic-gradient(from 45deg, #000 0 25%, #fff 0 50%, #000 0 75%, #fff 0)",
          backgroundSize: "16px 16px",
        },
      },
    ],
    []
  );

  const toPatternValue = (key) => `pattern:${key}`;

  const allItems = useMemo(
    () => [
      ...palette.map((c) => ({
        kind: "color",
        key: c.value,
        name: c.name,
        value: c.value,
        border: c.border,
      })),
      ...patterns.map((p) => ({
        kind: "pattern",
        key: p.key,
        name: p.name,
        value: toPatternValue(p.key),
        style: p.style,
      })),
    ],
    [palette, patterns]
  );

  const itemsByKind = useMemo(() => {
    if (kind === "colors") return allItems.filter((it) => it.kind === "color");
    if (kind === "patterns") return allItems.filter((it) => it.kind === "pattern");
    return activeTab === "colors"
      ? allItems.filter((it) => it.kind === "color")
      : allItems.filter((it) => it.kind === "pattern");
  }, [allItems, activeTab, kind]);

  // ===== значение и изменение =====
  const current = multiple
    ? Array.isArray(value)
      ? value
      : value
      ? [value]
      : []
    : value
    ? [value]
    : [];

  const toggleValue = (v) => {
    if (!multiple) return onChange?.(v);
    const set = new Set(current);
    set.has(v) ? set.delete(v) : set.add(v);
    onChange?.(Array.from(set));
  };

  const clearAll = () => onChange?.(multiple ? [] : undefined);

  // теги в Select — стабильное выравнивание через Tag.icon
  const tagRender = ({ value: v, closable, onClose }) => {
    const item = allItems.find((x) => x.value === v);
    const label = item?.name || v;
    return (
      <Tooltip title={label}>
        <Tag
          icon={<SwatchPreview item={item} size={12} />}
          closable={closable}
          onClose={onClose}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{ marginInlineEnd: 4 }}
        >
          {label}
        </Tag>
      </Tooltip>
    );
  };

  return (
    <>
      {/* Сам Select — просто поле с тегами. Выпадашку НЕ используем */}
      <Select
        ref={selectRef}
        mode={multiple ? "multiple" : undefined}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        allowClear={allowClear}
        maxTagCount={maxTagCount}
        tagRender={tagRender}
        showSearch={false}
        open={false}                     // важно: не открывать родной дропдаун
        onMouseDown={handleSelectMouseDown} // открываем наш Modal по клику
        style={{ width: "100%" }}
      />

      {/* Центрированный модальный пикер */}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        width={Math.min(420, typeof window !== "undefined" ? window.innerWidth - 24 : 420)}
        styles={{
          body: { padding: 12 },
          header: { marginBottom: 0 },
        }}
        title={kind === "both" ? "Колір / Узор" : kind === "colors" ? "Кольори" : "Узори"}
        destroyOnClose
        maskClosable
      >
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          {kind === "both" && (
            <Segmented
              block
              options={[
                { label: "Кольори", value: "colors" },
                { label: "Узори", value: "patterns" },
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
          )}

          <SwatchGrid items={itemsByKind} current={new Set(current)} onToggle={toggleValue} />

          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4 }}>
            {allowClear && (
              <Button size="small" onClick={clearAll}>
                Очистити
              </Button>
            )}
            <Button size="small" type="primary" onClick={() => setOpen(false)}>
              Готово
            </Button>
          </div>
        </Space>
      </Modal>
    </>
  );
}

/* Helpers */

function SwatchPreview({ item, size = 16 }) {
  if (!item)
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          background: "#ccc",
          display: "inline-block",
        }}
      />
    );

  const style =
    item.kind === "pattern"
      ? {
          width: size,
          height: size,
          borderRadius: 4,
          display: "inline-block",
          ...item.style,
        }
      : {
          width: size,
          height: size,
          borderRadius: 4,
          display: "inline-block",
          background: item.value,
          border: item.border ? "1px solid #d9d9d9" : "none",
        };

  return (
    <span
      style={{
        ...style,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
    />
  );
}

function SwatchGrid({ items, current, onToggle }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))",
        gap: 8,
        paddingTop: 4,
        maxHeight: 360,
        overflow: "auto",
      }}
    >
      {items.map((it) => {
        const selected = current.has(it.value);
        return (
          <button
            key={it.value}
            type="button"
            onClick={() => onToggle(it.value)}
            aria-pressed={selected}
            title={it.name}
            style={{
              border: selected ? "2px solid #1677ff" : "1px solid #d9d9d9",
              borderRadius: 10,
              padding: 8,
              aspectRatio: "1/1",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SwatchPreview item={it} size={24} />
          </button>
        );
      })}
    </div>
  );
}
